import type { CSVImportMapping } from "@pilatestime/shared-types";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle, FileText, Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	useProcessCSVImport,
	useValidateCSVImport,
} from "@/hooks/api/useCustomerImport";
import { useApiParameters } from "@/hooks/useApiParameters";
import { ColumnMapping } from "@/routes/_auth/app/_app/org/$orgId/settings/import/-components/ColumnMapping";
import { CSVTemplate } from "@/routes/_auth/app/_app/org/$orgId/settings/import/-components/CSVTemplate";
import { CSVUpload } from "@/routes/_auth/app/_app/org/$orgId/settings/import/-components/CSVUpload";
import { ImportPreview } from "@/routes/_auth/app/_app/org/$orgId/settings/import/-components/ImportPreview";
import { ImportProgress } from "@/routes/_auth/app/_app/org/$orgId/settings/import/-components/ImportProgress";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/import/customers",
)({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		return { userId, token };
	},
	component: RouteComponent,
});

type ImportStep = "upload" | "mapping" | "preview" | "import" | "complete";

function RouteComponent() {
	const { t } = useTranslation();
	Route.useLoaderData();
	const { orgId } = Route.useParams();
	const [currentStep, setCurrentStep] = useState<ImportStep>("upload");
	const [csvData, setCsvData] = useState<CSVImportMapping | null>(null);
	const [, setUploadError] = useState<string | null>(null);

	// API hooks
	const validateCSVMutation = useValidateCSVImport(orgId);
	const processCSVMutation = useProcessCSVImport(orgId);

	const handleGoBack = () => {
		window.history.back();
	};

	const steps = [
		{ id: "upload", label: t("import.steps.upload"), icon: Upload },
		{ id: "mapping", label: t("import.steps.mapping"), icon: FileText },
		{ id: "preview", label: t("import.steps.preview"), icon: CheckCircle },
		{ id: "import", label: t("import.steps.import"), icon: Upload },
	];

	const getStepStatus = (stepId: string) => {
		const stepIndex = steps.findIndex((s) => s.id === stepId);
		const currentIndex = steps.findIndex((s) => s.id === currentStep);

		if (stepIndex < currentIndex) return "completed";
		if (stepIndex === currentIndex) return "current";
		return "upcoming";
	};

	const handleFileUploaded = (data: CSVImportMapping) => {
		setCsvData(data);
		setUploadError(null);
		setCurrentStep("mapping");
	};

	const handleUploadError = (error: string) => {
		setUploadError(error);
		setCsvData(null);
	};

	const handleMappingComplete = (mapping: CSVImportMapping) => {
		setCsvData(mapping);
		setCurrentStep("preview");
	};

	const handleBackToUpload = () => {
		setCurrentStep("upload");
	};

	const handleBackToMapping = () => {
		setCurrentStep("mapping");
	};

	const handleStartImport = (
		data: CSVImportMapping,
		skipInvalidRows: boolean,
	) => {
		setCurrentStep("import");
		processCSVMutation.mutate({
			mapping: data,
			skipInvalidRows,
		});
	};

	const handleStartOver = () => {
		setCsvData(null);
		setUploadError(null);
		setCurrentStep("upload");
		validateCSVMutation.reset();
		processCSVMutation.reset();
	};

	const handleGoToCustomers = () => {
		window.location.href = `/app/org/${orgId}/customers`;
	};

	return (
		<div className="h-full flex flex-col overflow-auto">
			{/* Header */}
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center space-x-4">
					<Button
						variant="outline"
						size="sm"
						onClick={handleGoBack}
						className="btn btn-ghost btn-sm"
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						{t("common.back")}
					</Button>
					<div>
						<h1 className="text-2xl font-bold text-base-content">
							{t("import.customers.title")}
						</h1>
						<p className="text-base-content/60">
							{t("import.customers.description")}
						</p>
					</div>
				</div>
			</div>

			{/* Progress Steps */}
			<div className="mb-8">
				<div className="flex items-center justify-between">
					{steps.map((step, index) => {
						const status = getStepStatus(step.id);
						const Icon = step.icon;

						return (
							<div key={step.id} className="flex items-center">
								<div className="flex flex-col items-center">
									<div
										className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
											status === "completed"
												? "bg-primary border-primary text-primary-content"
												: status === "current"
													? "bg-primary/10 border-primary text-primary"
													: "bg-base-200 border-base-300 text-base-content/50"
										}`}
									>
										<Icon className="h-5 w-5" />
									</div>
									<span
										className={`mt-2 text-sm font-medium ${
											status === "current"
												? "text-primary"
												: status === "completed"
													? "text-primary"
													: "text-base-content/50"
										}`}
									>
										{step.label}
									</span>
								</div>
								{index < steps.length - 1 && (
									<div
										className={`flex-1 h-0.5 mx-4 ${
											status === "completed" ? "bg-primary" : "bg-base-300"
										}`}
									/>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Step Content */}
			<div className="space-y-6">
				{currentStep === "upload" && (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center space-x-2">
								<Upload className="h-5 w-5 text-primary" />
								<span>{t("import.upload.title")}</span>
							</CardTitle>
							<CardDescription>
								{t("import.upload.description")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="space-y-6">
								{/* CSV Upload Component */}
								<CSVUpload
									onFileUploaded={handleFileUploaded}
									onError={handleUploadError}
									maxFileSize={10}
								/>

								{/* Sample Template */}
								<CSVTemplate />

								{/* Requirements */}
								<div className="bg-info/10 border border-info/20 rounded-lg p-4">
									<h4 className="font-medium text-info mb-2">
										{t("import.upload.requirements")}
									</h4>
									<ul className="text-sm text-info/80 space-y-1">
										<li>• {t("import.upload.requirement1")}</li>
										<li>• {t("import.upload.requirement2")}</li>
										<li>• {t("import.upload.requirement3")}</li>
										<li>• {t("import.upload.requirement4")}</li>
									</ul>
								</div>
							</div>
						</CardContent>
					</Card>
				)}

				{currentStep === "mapping" && csvData && (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center space-x-2">
								<FileText className="h-5 w-5 text-primary" />
								<span>{t("import.mapping.title")}</span>
							</CardTitle>
							<CardDescription>
								{t("import.mapping.description")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ColumnMapping
								csvData={csvData}
								onMappingComplete={handleMappingComplete}
								onBack={handleBackToUpload}
							/>
						</CardContent>
					</Card>
				)}

				{currentStep === "preview" && csvData && (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center space-x-2">
								<CheckCircle className="h-5 w-5 text-primary" />
								<span>{t("import.preview.title")}</span>
							</CardTitle>
							<CardDescription>
								{t("import.preview.description")}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ImportPreview
								csvData={csvData}
								onStartImport={handleStartImport}
								onBack={handleBackToMapping}
							/>
						</CardContent>
					</Card>
				)}

				{currentStep === "import" && (
					<ImportProgress
						isLoading={processCSVMutation.isPending}
						result={processCSVMutation.data || null}
						error={processCSVMutation.error?.message || null}
						onStartOver={handleStartOver}
						onGoToCustomers={handleGoToCustomers}
					/>
				)}
			</div>
		</div>
	);
}
